/**
 * Column geometry and stored-layout reconciliation for `<DataTable>`.
 *
 * Deliberately free of React and of the DOM: the interesting failure modes here
 * are arithmetic ("the dragged column drifted by 3px") and data hygiene ("the
 * saved order lists a column that no longer exists"), and both are far easier to
 * pin down in a unit test than through a rendered table. `data-table.tsx` reads
 * the container width and hands it in; everything below is a pure function.
 */

/** Floor for a column left without a width — it absorbs the leftover space. */
export const FLEX_COLUMN_MIN_PX = 160;
/** Nobody can drag a column narrower than this, however hard they try. */
export const COLUMN_MIN_PX = 64;
/** …nor wider. Guards a corrupted `1e9` in storage as much as a stray drag. */
export const COLUMN_MAX_PX = 800;

/** Tailwind's spacing scale: one step is 0.25rem, at the default 16px root. */
const SPACING_STEP_PX = 4;
const ROOT_FONT_PX = 16;

/**
 * A declared width in pixels, or `null` for "this column absorbs the slack".
 *
 * Accepts the Tailwind classes the app's tables were written with (`w-64`,
 * `w-[220px]`) so a call site can be migrated to `columns` without also
 * restating every width as a number. Anything unrecognised reads as `null`,
 * which is what the class-based colgroup effectively did with it anyway.
 */
export function normalizeColumnWidth(
  width?: number | string | null,
): number | null {
  if (width === null || width === undefined) return null;
  if (typeof width === 'number') {
    return Number.isFinite(width) && width > 0 ? width : null;
  }
  const scale = /^w-(\d+(?:\.\d+)?)$/.exec(width);
  if (scale) return Number(scale[1]) * SPACING_STEP_PX;
  const arbitrary = /^w-\[(\d+(?:\.\d+)?)(px|rem)\]$/.exec(width);
  if (arbitrary) {
    return Number(arbitrary[1]) * (arbitrary[2] === 'rem' ? ROOT_FONT_PX : 1);
  }
  return null;
}

export function clampColumnWidth(
  width: number,
  bounds?: { minWidth?: number; maxWidth?: number },
): number {
  const min = bounds?.minWidth ?? COLUMN_MIN_PX;
  const max = Math.max(min, bounds?.maxWidth ?? COLUMN_MAX_PX);
  return Math.round(Math.min(Math.max(width, min), max));
}

export interface ResolveColumnWidthsInput {
  /** Per column: its width in px, or `null` for "absorbs the leftover space". */
  base: (number | null)[];
  /** Per column: `true` when the width above came from a user drag. */
  userSized?: boolean[];
  /** Per column: `true` for `pinned: 'end'`, which never absorbs slack. */
  pinnedEnd?: boolean[];
  /** The scroll container's `clientWidth`; `0` means "not measured yet". */
  containerWidth: number;
  /** Index of the column currently under the pointer, if a drag is in flight. */
  activeResizeIndex?: number;
}

/**
 * Turns declared widths into the explicit pixel array the `<colgroup>` renders.
 *
 * Every `<col>` ends up with a definite width and the table is sized to their
 * exact sum, because `table-layout: fixed` with `width: 100%` redistributes any
 * leftover space across *all* columns — which would make a column visibly drift
 * away from the width the reader just dragged it to.
 *
 * Exactly one column absorbs the difference between the declared widths and the
 * container, and which one is chosen has to be stable across a drag or the
 * layout fights the reader:
 *
 * 1. the column the developer left without a width (the common case — nothing
 *    is stored yet, and this reproduces the pre-resize layout to the pixel);
 * 2. failing that — the reader has dragged that column, so it now has a width —
 *    the last column that is neither pinned to the end nor sized by the reader,
 *    so absorbing never overrides a width somebody chose on purpose;
 * 3. failing that, nobody: every column keeps its width and the table simply
 *    ends before the container does.
 *
 * The column being dragged is never the absorber, otherwise it would swallow
 * its own resize and refuse to move.
 *
 * Returns `null` when the container has not been measured (first paint, and
 * jsdom, which has no `ResizeObserver`) so the caller can fall back to the
 * class-based colgroup rather than render every column at zero.
 */
export function resolveColumnWidths({
  base,
  userSized,
  pinnedEnd,
  containerWidth,
  activeResizeIndex,
}: ResolveColumnWidthsInput): number[] | null {
  if (base.length === 0) return null;
  if (!Number.isFinite(containerWidth) || containerWidth <= 0) return null;

  const flexIndexes = base.flatMap((width, index) =>
    width === null ? [index] : [],
  );
  const fixed = base.map((width) =>
    width === null ? FLEX_COLUMN_MIN_PX : width,
  );

  if (flexIndexes.length > 0) {
    const others = fixed.reduce(
      (sum, width, index) => (flexIndexes.includes(index) ? sum : sum + width),
      0,
    );
    const share = Math.floor((containerWidth - others) / flexIndexes.length);
    const resolved = [...fixed];
    for (const index of flexIndexes) {
      resolved[index] = Math.max(FLEX_COLUMN_MIN_PX, share);
    }
    return resolved;
  }

  const total = fixed.reduce((sum, width) => sum + width, 0);
  const leftover = containerWidth - total;
  if (leftover <= 0) return fixed;

  const absorber = lastIndexWhere(
    fixed,
    (_width, index) =>
      index !== activeResizeIndex && !pinnedEnd?.[index] && !userSized?.[index],
  );
  if (absorber < 0) return fixed;

  const resolved = [...fixed];
  resolved[absorber] += leftover;
  return resolved;
}

function lastIndexWhere<T>(
  items: T[],
  predicate: (item: T, index: number) => boolean,
): number {
  for (let index = items.length - 1; index >= 0; index -= 1) {
    if (predicate(items[index], index)) return index;
  }
  return -1;
}

interface OrderableColumn {
  id: string;
  pinned?: 'start' | 'end';
}

/**
 * The column order to render, given what is in storage and what the code ships.
 *
 * Tolerant on purpose: `null`, junk, an order saved before a column was renamed
 * and an order saved before a column was pinned all have to resolve to a usable
 * table rather than an exception on a screen the reader cannot leave.
 *
 * A column the saved order has never heard of — added in a later release, or
 * hidden last render because the reader lacked write access — is put back
 * *next to the neighbour it was declared after*, not appended. Appending would
 * fling a newly inserted middle column to the far right of every existing
 * reader's table.
 */
export function reconcileColumnOrder(
  savedOrder: unknown,
  columns: OrderableColumn[],
): string[] {
  const known = new Map(columns.map((column) => [column.id, column]));
  const seen = new Set<string>();
  const order = Array.isArray(savedOrder)
    ? savedOrder.filter((id): id is string => {
        if (typeof id !== 'string' || !known.has(id) || seen.has(id)) {
          return false;
        }
        seen.add(id);
        return true;
      })
    : [];

  columns.forEach((column, index) => {
    if (seen.has(column.id)) return;
    let anchor = -1;
    for (let before = index - 1; before >= 0 && anchor < 0; before -= 1) {
      anchor = order.indexOf(columns[before].id);
    }
    order.splice(anchor + 1, 0, column.id);
    seen.add(column.id);
  });

  const pinnedAt = (side: 'start' | 'end') =>
    columns.flatMap((column) => (column.pinned === side ? [column.id] : []));
  const pinned = new Set([...pinnedAt('start'), ...pinnedAt('end')]);
  return [
    ...pinnedAt('start'),
    ...order.filter((id) => !pinned.has(id)),
    ...pinnedAt('end'),
  ];
}

interface SizableColumn {
  id: string;
  minWidth?: number;
  maxWidth?: number;
}

/** Saved widths, minus ids we no longer render and values we cannot honour. */
export function reconcileColumnWidths(
  savedWidths: unknown,
  columns: SizableColumn[],
): Record<string, number> {
  if (
    savedWidths === null ||
    typeof savedWidths !== 'object' ||
    Array.isArray(savedWidths)
  ) {
    return {};
  }
  const entries = savedWidths as Record<string, unknown>;
  const widths: Record<string, number> = {};
  for (const column of columns) {
    const width = entries[column.id];
    if (typeof width !== 'number' || !Number.isFinite(width)) continue;
    widths[column.id] = clampColumnWidth(width, column);
  }
  return widths;
}

/** Order with `activeId` lifted out and dropped where `overId` sits. */
export function moveColumn(
  order: string[],
  activeId: string,
  overId: string,
): string[] {
  const from = order.indexOf(activeId);
  const to = order.indexOf(overId);
  if (from < 0 || to < 0 || from === to) return order;
  const next = [...order];
  next.splice(from, 1);
  next.splice(to, 0, activeId);
  return next;
}
